import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from './Toast';
import './RoleFormModal.css';

function RoleFormModal({ role, menus, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
    menuIds: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        active: role.active !== undefined ? role.active : true,
        menuIds: role.menus ? role.menus.map(m => m.id) : []
      });
    }
  }, [role]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMenuToggle = (menuId) => {
    const clickedMenu = menus.find(m => m.id === menuId || String(m.id) === String(menuId));
    if (!clickedMenu) return;

    setFormData(prev => {
      const isCurrentlySelected = prev.menuIds.includes(menuId);
      let newMenuIds = [...prev.menuIds];

      if (isCurrentlySelected) {
        // Deselecting
        newMenuIds = newMenuIds.filter(id => id !== menuId);
        
        // If we deselected a parent menu, automatically deselect all its submenus
        if (!clickedMenu.parentId) {
          const submenus = menus.filter(m => m.parentId === menuId || String(m.parentId) === String(menuId));
          const subIds = submenus.map(m => m.id);
          newMenuIds = newMenuIds.filter(id => !subIds.includes(id));
        } else {
          // If we deselected a submenu, check if there are any remaining selected submenus under the same parent
          const parentId = clickedMenu.parentId;
          const siblingSubmenus = menus.filter(m => m.parentId === parentId || String(m.parentId) === String(parentId));
          const hasRemainingSiblings = siblingSubmenus.some(m => newMenuIds.includes(m.id));
          
          if (!hasRemainingSiblings) {
            // Uncheck the parent menu if all its submenus are now unchecked
            newMenuIds = newMenuIds.filter(id => id !== parentId && String(id) !== String(parentId));
          }
        }
      } else {
        // Selecting
        newMenuIds.push(menuId);

        // If we selected a submenu, automatically ensure parent menu is selected
        if (clickedMenu.parentId) {
          const parentMenu = menus.find(m => m.id === clickedMenu.parentId || String(m.id) === String(clickedMenu.parentId));
          if (parentMenu && !newMenuIds.includes(parentMenu.id)) {
            newMenuIds.push(parentMenu.id);
          }
        }
      }

      return {
        ...prev,
        menuIds: newMenuIds
      };
    });
  };

  const handleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      menuIds: menus.map(m => m.id)
    }));
  };

  const handleDeselectAll = () => {
    setFormData(prev => ({
      ...prev,
      menuIds: []
    }));
  };

  const getMenuIcon = (menuKey) => {
    const icons = {
      'dashboard': '📊',
      'enquiries': '📧',
      'customers': '👥',
      'projects': '🏗️',
      'expenses': '💰',
      'vendors': '🏪',
      'employees': '👤',
      'payroll': '💵',
      'reports': '📈',
      'users': '👨‍💼',
      'roles': '🔐',
      'audit-logs': '📜'
    };
    return icons[menuKey] || '📄';
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Role name must not exceed 50 characters';
    }

    if (formData.description && formData.description.length > 255) {
      newErrors.description = 'Description must not exceed 255 characters';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      if (role) {
        // Update existing role
        await axios.put(`/roles/${role.id}`, formData);
      } else {
        // Create new role
        await axios.post('/roles', formData);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  // Build menu hierarchy
  const topLevelMenus = menus.filter(m => !m.parentId);
  const getSubMenus = (parentId) => {
    return menus.filter(m => m.parentId === parentId || String(m.parentId) === String(parentId));
  };
  const renderedMenuIds = new Set();
  topLevelMenus.forEach(m => {
    renderedMenuIds.add(m.id);
    getSubMenus(m.id).forEach(sub => renderedMenuIds.add(sub.id));
  });
  const orphanMenus = menus.filter(m => !renderedMenuIds.has(m.id));

  return (
    <div className="role-modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{role ? 'Edit Role' : 'Create New Role'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="name">Role Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                disabled={role?.isSystemRole}
                placeholder="e.g., ACCOUNTANT"
                required
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={errors.description ? 'error' : ''}
                rows="3"
                placeholder="Brief description of this role"
              />
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
                <span>Active</span>
              </label>
            </div>

            <div className="form-group">
              <div className="menu-header">
                <label>Assigned Menus</label>
                <div className="menu-actions">
                  <button type="button" className="btn-link" onClick={handleSelectAll}>
                    Select All
                  </button>
                  <button type="button" className="btn-link" onClick={handleDeselectAll}>
                    Clear All
                  </button>
                </div>
              </div>
              <div className="menu-selection-info">
                <span className="selected-count">
                  {formData.menuIds.length} of {menus.length} menus selected
                </span>
              </div>
              <div className="menu-tree-container">
                {topLevelMenus.map(parentMenu => {
                  const subMenus = getSubMenus(parentMenu.id);
                  return (
                    <div key={parentMenu.id} className="menu-group">
                      {/* Parent Menu Card */}
                      <div className="parent-menu-wrapper">
                        <div
                          className={`menu-card parent-card ${formData.menuIds.includes(parentMenu.id) ? 'selected' : ''}`}
                          onClick={() => handleMenuToggle(parentMenu.id)}
                          title={`Label: ${parentMenu.label}\nPath: ${parentMenu.path || '-'}\nKey: ${parentMenu.key}`}
                        >
                          <div className="menu-card-icon">
                            {getMenuIcon(parentMenu.key)}
                          </div>
                          <div className="menu-card-content">
                            <div className="menu-card-title">{parentMenu.label}</div>
                            <div className="menu-card-path">{parentMenu.path || 'No Path (Group Header)'}</div>
                          </div>
                          <div className="menu-card-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.menuIds.includes(parentMenu.id)}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sub-menus Grid (if any) */}
                      {subMenus.length > 0 && (
                        <div className="sub-menus-grid">
                          {subMenus.map(subMenu => (
                            <div
                              key={subMenu.id}
                              className={`menu-card sub-card ${formData.menuIds.includes(subMenu.id) ? 'selected' : ''}`}
                              onClick={() => handleMenuToggle(subMenu.id)}
                              title={`Label: ${subMenu.label}\nPath: ${subMenu.path || '-'}\nKey: ${subMenu.key}`}
                            >
                              <div className="menu-card-icon">
                                {getMenuIcon(subMenu.key)}
                              </div>
                              <div className="menu-card-content">
                                <div className="menu-card-title">{subMenu.label}</div>
                                <div className="menu-card-path">{subMenu.path}</div>
                              </div>
                              <div className="menu-card-checkbox">
                                <input
                                  type="checkbox"
                                  checked={formData.menuIds.includes(subMenu.id)}
                                  readOnly
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Orphan Menus (if any) */}
                {orphanMenus.length > 0 && (
                  <div className="menu-group">
                    <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#666' }}>Other Menus</h4>
                    <div className="sub-menus-grid" style={{ paddingLeft: 0, borderLeft: 'none', marginLeft: 0 }}>
                      {orphanMenus.map(subMenu => (
                        <div
                          key={subMenu.id}
                          className={`menu-card sub-card ${formData.menuIds.includes(subMenu.id) ? 'selected' : ''}`}
                          onClick={() => handleMenuToggle(subMenu.id)}
                          title={`Label: ${subMenu.label}\nPath: ${subMenu.path || '-'}\nKey: ${subMenu.key}`}
                        >
                          <div className="menu-card-icon">
                            {getMenuIcon(subMenu.key)}
                          </div>
                          <div className="menu-card-content">
                            <div className="menu-card-title">{subMenu.label}</div>
                            <div className="menu-card-path">{subMenu.path}</div>
                          </div>
                          <div className="menu-card-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.menuIds.includes(subMenu.id)}
                              readOnly
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RoleFormModal;
